/** Leitura em voz alta sobre a camada de texto (US-03 / ADR-009).
 *
 * A `SpeechSynthesis` é uma API cheia de arestas, e quase todo o código aqui
 * existe por causa de uma delas:
 *
 * - **`getVoices()` volta vazio na primeira chamada** no Chrome: as vozes
 *   carregam de forma assíncrona e só então dispara `voiceschanged`. Ler uma
 *   vez no mount daria sempre a voz padrão do sistema.
 * - **Enunciado longo trava** (ver `utils/tts.ts`): daí a fila de pedaços
 *   encadeada no `onend`.
 * - **`cancel()` não é opcional.** Sem ele no desmonte, ao virar a página e ao
 *   trocar de livro, a voz continua lendo a página anterior sobre a nova — e,
 *   pior, lendo em cima de uma página que pode não estar liberada.
 * - **A fala sobrevive à navegação** porque `speechSynthesis` é do `window`,
 *   não do componente. Quem monta, cancela.
 *
 * O texto vem da camada JÁ RENDERIZADA, nunca de `getTextContent()`: a camada
 * só existe para página liberada, então a restrição de liberação é estrutural
 * em vez de depender de mais uma checagem que alguém pode esquecer.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import { MAX_CARACTERES, escolherVoz, fatiarEmFrases } from '@/features/reader/utils/tts'

/** Porta injetável — o jsdom não implementa `speechSynthesis` de verdade. */
export interface SinteseDeVoz {
  speak: (enunciado: SpeechSynthesisUtterance) => void
  cancel: () => void
  getVoices: () => SpeechSynthesisVoice[]
  addEventListener: (tipo: 'voiceschanged', ouvinte: () => void) => void
  removeEventListener: (tipo: 'voiceschanged', ouvinte: () => void) => void
}

export type FabricaDeEnunciado = (texto: string) => SpeechSynthesisUtterance

export type EstadoDaFala = 'parado' | 'falando'

interface UseTextToSpeechOptions {
  /** Velocidade da fala (0,5 a 2), da preferência `tts_rate`. */
  velocidade?: number
  /** Muda de valor quando o contexto muda (página, livro): cancela a fala. */
  contexto?: unknown
  sintese?: SinteseDeVoz | null
  criarEnunciado?: FabricaDeEnunciado
}

function sinteseDoNavegador(): SinteseDeVoz | null {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return null
  return window.speechSynthesis
}

function enunciadoDoNavegador(texto: string): SpeechSynthesisUtterance {
  return new SpeechSynthesisUtterance(texto)
}

export function useTextToSpeech({
  velocidade = 1,
  contexto,
  sintese,
  criarEnunciado = enunciadoDoNavegador,
}: UseTextToSpeechOptions = {}) {
  // `??` aqui seria errado: o tipo promete que `null` significa "sem síntese",
  // e `??` faria o `null` explícito cair no motor do navegador — que em teste
  // existe como stub, mascarando o caminho "navegador sem TTS".
  const [motor] = useState<SinteseDeVoz | null>(() =>
    sintese === undefined ? sinteseDoNavegador() : sintese,
  )
  const [estado, setEstado] = useState<EstadoDaFala>('parado')
  const [voz, setVoz] = useState<SpeechSynthesisVoice | null>(null)

  // `sessao` invalida os callbacks de um `speak` anterior: sem ele, o `onend` de
  // um pedaço já cancelado ressuscitaria a fila e a leitura recomeçaria sozinha.
  const sessao = useRef(0)
  const parametros = useRef({ velocidade, voz, criarEnunciado, motor })
  useEffect(() => {
    parametros.current = { velocidade, voz, criarEnunciado, motor }
  })

  // As vozes chegam depois: ler só no mount daria sempre a voz do sistema, e
  // um Chromebook de escola em inglês leria português com fonética inglesa.
  useEffect(() => {
    if (!motor) return
    const aplicar = () => setVoz(escolherVoz(motor.getVoices()))
    aplicar()
    motor.addEventListener('voiceschanged', aplicar)
    return () => motor.removeEventListener('voiceschanged', aplicar)
  }, [motor])

  const parar = useCallback(() => {
    sessao.current += 1
    parametros.current.motor?.cancel()
    setEstado('parado')
  }, [])

  const falar = useCallback(
    (texto: string) => {
      const { motor: atual, velocidade: taxa, voz: vozAtual, criarEnunciado: criar } =
        parametros.current
      if (!atual) return

      const pedacos = fatiarEmFrases(texto, MAX_CARACTERES)
      if (pedacos.length === 0) return

      // Cancelar ANTES de enfileirar: `speak` sem `cancel` empilha na fila do
      // navegador, e dois cliques no botão leriam a página duas vezes seguidas.
      atual.cancel()
      sessao.current += 1
      const minha = sessao.current
      setEstado('falando')

      const dizer = (indice: number) => {
        if (sessao.current !== minha) return
        const pedaco = pedacos[indice]
        if (pedaco === undefined) {
          setEstado('parado')
          return
        }
        const enunciado = criar(pedaco)
        enunciado.lang = 'pt-BR'
        enunciado.rate = taxa
        if (vozAtual) enunciado.voice = vozAtual
        enunciado.onend = () => dizer(indice + 1)
        // Erro no meio da fila para a leitura em vez de pular para o pedaço
        // seguinte: continuar daria um salto silencioso no texto, que é pior
        // para quem depende da voz do que a leitura simplesmente parar.
        enunciado.onerror = () => {
          if (sessao.current === minha) setEstado('parado')
        }
        atual.speak(enunciado)
      }

      dizer(0)
    },
    [],
  )

  // Virar a página, trocar de livro ou desmontar cancela: sem isto a voz segue
  // lendo a página anterior sobre a nova — que pode nem estar liberada.
  useEffect(() => () => parar(), [contexto, parar])

  return {
    falar,
    parar,
    estado,
    /** `false` = navegador sem síntese de voz; o botão não deve aparecer. */
    disponivel: motor !== null,
    voz,
  }
}
